import { supabase } from './supabase';
import {
  parseAutomationStepRef,
  parseAutomationWorkflow,
  type MapAutomationStepRef,
  type MapAutomationWorkflow,
} from './systemMapAutomation';

export type {
  MapAutomationStep,
  MapAutomationStepRef,
  MapAutomationWorkflow,
} from './systemMapAutomation';
export {
  automationNodeDefaultSize,
  automationParentChainSize,
  captureAllAutomationStepPreviews,
  captureAutomationStepPreviewScreenshot,
  describeAutomationSchedule,
  linkMapNodeToAutomation,
  listAutomationsForMap,
  materializeAutomationStepChain,
  syncMapNodeFromAutomation,
  uploadAutomationStepScreenshot,
} from './systemMapAutomation';

/** Supabase Storage bucket for system-map node screenshots. */
export const SYSTEM_MAP_STORAGE_BUCKET = 'system-map';

export function systemMapScreenshotPublicUrl(slug: string, ext = '.webp'): string {
  const base = import.meta.env.VITE_SUPABASE_URL?.replace(/\/$/, '') ?? '';
  const path = `${slug}${ext}`;
  const { data } = supabase.storage.from(SYSTEM_MAP_STORAGE_BUCKET).getPublicUrl(path);
  return data.publicUrl || `${base}/storage/v1/object/public/${SYSTEM_MAP_STORAGE_BUCKET}/${path}`;
}

export const MAP_NODE_KINDS = [
  'workflow',
  'content',
  'site',
  'infra',
  'integration',
  'repo',
  'automation',
  'automation_step',
] as const;
export type MapNodeKind = (typeof MAP_NODE_KINDS)[number];

export const MAP_NODE_LAYERS = ['public', 'ops', 'editorial', 'private'] as const;
export type MapNodeLayer = (typeof MAP_NODE_LAYERS)[number];

export const MAP_EDGE_TYPES = ['cta', 'api', 'data', 'redirect', 'deploy'] as const;
export type MapEdgeType = (typeof MAP_EDGE_TYPES)[number];

export interface MapViewport {
  x: number;
  y: number;
  zoom: number;
}

export interface MapAnnotations {
  notes?: string;
  automation?: MapAutomationWorkflow;
  automationStep?: MapAutomationStepRef;
}

export interface MapEdgeWaypoint {
  x: number;
  y: number;
}

export const MAP_ANCHOR_SIDES = ['top', 'right', 'bottom', 'left'] as const;
export type MapAnchorSide = (typeof MAP_ANCHOR_SIDES)[number];

export interface MapEdgeAnchor {
  side: MapAnchorSide;
  /** 0–1 position along the chosen side (top/bottom: left→right; left/right: top→bottom). */
  offset: number;
}

export interface MapEdgeMetadata {
  waypoints?: MapEdgeWaypoint[];
  sourceAnchor?: MapEdgeAnchor;
  targetAnchor?: MapEdgeAnchor;
}

export interface SystemMapRow {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  viewport: MapViewport | null;
  created_at: string;
  updated_at: string;
}

export interface MapNodeRow {
  id: string;
  map_id: string;
  slug: string;
  label: string;
  kind: MapNodeKind;
  layer: MapNodeLayer;
  role_description: string | null;
  usage_notes: string | null;
  url: string | null;
  repo_path: string | null;
  x: number;
  y: number;
  width: number | null;
  height: number | null;
  show_thumbnail: boolean;
  screenshot_url: string | null;
  captured_at: string | null;
  source_hash: string | null;
  annotations: MapAnnotations;
  style: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface MapEdgeRow {
  id: string;
  map_id: string;
  source_node_id: string;
  target_node_id: string;
  label: string | null;
  edge_type: MapEdgeType;
  metadata: MapEdgeMetadata;
  created_at: string;
  updated_at: string;
}

export interface SystemMapBundle {
  map: SystemMapRow;
  nodes: MapNodeRow[];
  edges: MapEdgeRow[];
}

export const DEFAULT_MAP_SLUG = 'lccto-production';

function parseViewport(raw: unknown): MapViewport | null {
  if (!raw || typeof raw !== 'object') return null;
  const v = raw as Record<string, unknown>;
  const x = Number(v.x);
  const y = Number(v.y);
  const zoom = Number(v.zoom);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(zoom)) return null;
  return { x, y, zoom };
}

function parseAnnotations(raw: unknown): MapAnnotations {
  if (!raw || typeof raw !== 'object') return {};
  const a = raw as Record<string, unknown>;
  const automation = parseAutomationWorkflow(a.automation);
  const automationStep = parseAutomationStepRef(a.automationStep);
  return {
    notes: typeof a.notes === 'string' ? a.notes : undefined,
    automation,
    automationStep,
  };
}

function parseEdgeAnchor(raw: unknown): MapEdgeAnchor | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const a = raw as Record<string, unknown>;
  const side = String(a.side ?? '');
  const offset = Number(a.offset);
  if (!MAP_ANCHOR_SIDES.includes(side as MapAnchorSide) || !Number.isFinite(offset)) return undefined;
  return { side: side as MapAnchorSide, offset: Math.max(0, Math.min(1, offset)) };
}

function parseEdgeMetadata(raw: unknown): MapEdgeMetadata {
  if (!raw || typeof raw !== 'object') return {};
  const m = raw as Record<string, unknown>;
  const metadata: MapEdgeMetadata = {};

  const waypointsRaw = m.waypoints;
  if (Array.isArray(waypointsRaw)) {
    const waypoints = waypointsRaw
      .map((wp) => {
        if (!wp || typeof wp !== 'object') return null;
        const p = wp as Record<string, unknown>;
        const x = Number(p.x);
        const y = Number(p.y);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
        return { x, y };
      })
      .filter((wp): wp is MapEdgeWaypoint => wp != null);
    if (waypoints.length > 0) metadata.waypoints = waypoints;
  }

  const sourceAnchor = parseEdgeAnchor(m.sourceAnchor);
  if (sourceAnchor) metadata.sourceAnchor = sourceAnchor;

  const targetAnchor = parseEdgeAnchor(m.targetAnchor);
  if (targetAnchor) metadata.targetAnchor = targetAnchor;

  return metadata;
}

export const DEFAULT_SOURCE_ANCHOR: MapEdgeAnchor = { side: 'right', offset: 0.5 };
export const DEFAULT_TARGET_ANCHOR: MapEdgeAnchor = { side: 'left', offset: 0.5 };

export function getEdgeSourceAnchor(metadata: MapEdgeMetadata): MapEdgeAnchor {
  return metadata.sourceAnchor ?? DEFAULT_SOURCE_ANCHOR;
}

export function getEdgeTargetAnchor(metadata: MapEdgeMetadata): MapEdgeAnchor {
  return metadata.targetAnchor ?? DEFAULT_TARGET_ANCHOR;
}

export function mergeEdgeMetadata(
  current: MapEdgeMetadata,
  patch: Partial<MapEdgeMetadata>,
): MapEdgeMetadata {
  const next: MapEdgeMetadata = { ...current };

  if ('waypoints' in patch) {
    if (patch.waypoints && patch.waypoints.length > 0) next.waypoints = patch.waypoints;
    else delete next.waypoints;
  }
  if ('sourceAnchor' in patch) {
    if (patch.sourceAnchor) next.sourceAnchor = patch.sourceAnchor;
    else delete next.sourceAnchor;
  }
  if ('targetAnchor' in patch) {
    if (patch.targetAnchor) next.targetAnchor = patch.targetAnchor;
    else delete next.targetAnchor;
  }

  return next;
}

export function getEdgeWaypoints(metadata: MapEdgeMetadata): MapEdgeWaypoint[] {
  return metadata.waypoints ?? [];
}

export function mapSystemMapRow(raw: Record<string, unknown>): SystemMapRow {
  return {
    id: String(raw.id),
    slug: String(raw.slug ?? ''),
    name: String(raw.name ?? ''),
    description: raw.description != null ? String(raw.description) : null,
    viewport: parseViewport(raw.viewport),
    created_at: String(raw.created_at ?? ''),
    updated_at: String(raw.updated_at ?? ''),
  };
}

export function mapMapNodeRow(raw: Record<string, unknown>): MapNodeRow {
  const kindRaw = String(raw.kind ?? 'workflow');
  const layerRaw = String(raw.layer ?? 'public');
  return {
    id: String(raw.id),
    map_id: String(raw.map_id),
    slug: String(raw.slug ?? ''),
    label: String(raw.label ?? ''),
    kind: MAP_NODE_KINDS.includes(kindRaw as MapNodeKind) ? (kindRaw as MapNodeKind) : 'workflow',
    layer: MAP_NODE_LAYERS.includes(layerRaw as MapNodeLayer) ? (layerRaw as MapNodeLayer) : 'public',
    role_description: raw.role_description != null ? String(raw.role_description) : null,
    usage_notes: raw.usage_notes != null ? String(raw.usage_notes) : null,
    url: raw.url != null ? String(raw.url) : null,
    repo_path: raw.repo_path != null ? String(raw.repo_path) : null,
    x: Number(raw.x ?? 0),
    y: Number(raw.y ?? 0),
    width: raw.width != null ? Number(raw.width) : null,
    height: raw.height != null ? Number(raw.height) : null,
    show_thumbnail: Boolean(raw.show_thumbnail),
    screenshot_url: raw.screenshot_url != null ? String(raw.screenshot_url) : null,
    captured_at: raw.captured_at != null ? String(raw.captured_at) : null,
    source_hash: raw.source_hash != null ? String(raw.source_hash) : null,
    annotations: parseAnnotations(raw.annotations),
    style: raw.style && typeof raw.style === 'object' ? (raw.style as Record<string, unknown>) : {},
    created_at: String(raw.created_at ?? ''),
    updated_at: String(raw.updated_at ?? ''),
  };
}

export function mapMapEdgeRow(raw: Record<string, unknown>): MapEdgeRow {
  const edgeTypeRaw = String(raw.edge_type ?? 'data');
  return {
    id: String(raw.id),
    map_id: String(raw.map_id),
    source_node_id: String(raw.source_node_id),
    target_node_id: String(raw.target_node_id),
    label: raw.label != null ? String(raw.label) : null,
    edge_type: MAP_EDGE_TYPES.includes(edgeTypeRaw as MapEdgeType) ? (edgeTypeRaw as MapEdgeType) : 'data',
    metadata: parseEdgeMetadata(raw.metadata),
    created_at: String(raw.created_at ?? ''),
    updated_at: String(raw.updated_at ?? ''),
  };
}

export function kindLabel(kind: MapNodeKind): string {
  const labels: Record<MapNodeKind, string> = {
    workflow: 'Workflow',
    content: 'Content',
    site: 'Site',
    infra: 'Infra',
    integration: 'Integration',
    repo: 'Repo',
    automation: 'Automation',
    automation_step: 'Automation step',
  };
  return labels[kind];
}

export function layerLabel(layer: MapNodeLayer): string {
  const labels: Record<MapNodeLayer, string> = {
    public: 'Public',
    ops: 'Ops',
    editorial: 'Editorial',
    private: 'Private',
  };
  return labels[layer];
}

export function edgeTypeLabel(edgeType: MapEdgeType): string {
  const labels: Record<MapEdgeType, string> = {
    cta: 'CTA',
    api: 'API',
    data: 'Data',
    redirect: 'Redirect',
    deploy: 'Deploy',
  };
  return labels[edgeType];
}

export async function fetchSystemMap(slug = DEFAULT_MAP_SLUG): Promise<SystemMapBundle> {
  const { data: mapData, error: mapError } = await supabase
    .from('system_maps')
    .select('*')
    .eq('slug', slug)
    .maybeSingle();

  if (mapError) throw mapError;
  if (!mapData) throw new Error(`System map not found: ${slug}`);

  const map = mapSystemMapRow(mapData as Record<string, unknown>);

  const [nodesResult, edgesResult] = await Promise.all([
    supabase.from('map_nodes').select('*').eq('map_id', map.id).order('label'),
    supabase.from('map_edges').select('*').eq('map_id', map.id),
  ]);

  if (nodesResult.error) throw nodesResult.error;
  if (edgesResult.error) throw edgesResult.error;

  return {
    map,
    nodes: (nodesResult.data ?? []).map((row) => mapMapNodeRow(row as Record<string, unknown>)),
    edges: (edgesResult.data ?? []).map((row) => mapMapEdgeRow(row as Record<string, unknown>)),
  };
}

export async function updateNodePosition(nodeId: string, x: number, y: number): Promise<void> {
  const { error } = await supabase.from('map_nodes').update({ x, y }).eq('id', nodeId);
  if (error) throw error;
}

export async function updateNodeSize(nodeId: string, width: number, height: number): Promise<void> {
  const { error } = await supabase
    .from('map_nodes')
    .update({ width: Math.round(width), height: Math.round(height) })
    .eq('id', nodeId);
  if (error) throw error;
}

export async function updateMapViewport(mapId: string, viewport: MapViewport): Promise<void> {
  const { error } = await supabase.from('system_maps').update({ viewport }).eq('id', mapId);
  if (error) throw error;
}

export async function updateNodeAnnotations(
  nodeId: string,
  annotations: MapAnnotations,
): Promise<MapNodeRow> {
  const { data, error } = await supabase
    .from('map_nodes')
    .update({ annotations })
    .eq('id', nodeId)
    .select('*')
    .single();

  if (error) throw error;
  return mapMapNodeRow(data as Record<string, unknown>);
}

export async function updateMapNodeKind(nodeId: string, kind: MapNodeKind): Promise<MapNodeRow> {
  const { data, error } = await supabase
    .from('map_nodes')
    .update({ kind })
    .eq('id', nodeId)
    .select('*')
    .single();

  if (error) throw error;
  return mapMapNodeRow(data as Record<string, unknown>);
}

export interface CreateMapNodeInput {
  mapId: string;
  label: string;
  slug: string;
  kind: MapNodeKind;
  layer?: MapNodeLayer;
  url?: string | null;
  repo_path?: string | null;
  role_description?: string | null;
  usage_notes?: string | null;
  x?: number;
  y?: number;
  show_thumbnail?: boolean;
}

export function slugifyMapNodeLabel(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function uniqueMapNodeSlug(base: string, existingSlugs: string[]): string {
  const normalized = slugifyMapNodeLabel(base) || 'node';
  const taken = new Set(existingSlugs.map((s) => s.toLowerCase()));
  if (!taken.has(normalized)) return normalized;
  let i = 2;
  while (taken.has(`${normalized}-${i}`)) i += 1;
  return `${normalized}-${i}`;
}

export function defaultNewNodePosition(
  viewport: MapViewport | null,
  nodeCount: number,
): { x: number; y: number } {
  const baseX = viewport ? -viewport.x / viewport.zoom + 280 : 320;
  const baseY = viewport ? -viewport.y / viewport.zoom + 160 : 200;
  const offset = (nodeCount % 12) * 28;
  return { x: baseX + offset, y: baseY + offset };
}

export async function createMapNode(input: CreateMapNodeInput): Promise<MapNodeRow> {
  const payload = {
    map_id: input.mapId,
    slug: input.slug,
    label: input.label.trim(),
    kind: input.kind,
    layer: input.layer ?? 'public',
    url: input.url ?? null,
    repo_path: input.repo_path ?? null,
    role_description: input.role_description ?? null,
    usage_notes: input.usage_notes ?? null,
    x: input.x ?? 0,
    y: input.y ?? 0,
    show_thumbnail: input.show_thumbnail ?? false,
  };

  const { data, error } = await supabase.from('map_nodes').insert(payload).select('*').single();
  if (error) throw error;
  return mapMapNodeRow(data as Record<string, unknown>);
}

function screenshotExtForFile(file: File): string {
  if (file.type === 'image/png') return '.png';
  if (file.type === 'image/webp') return '.webp';
  if (file.type === 'image/jpeg' || file.type === 'image/jpg') return '.jpg';
  const name = file.name.toLowerCase();
  if (name.endsWith('.png')) return '.png';
  if (name.endsWith('.webp')) return '.webp';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return '.jpg';
  return '.png';
}

export async function uploadMapNodeScreenshot(
  nodeId: string,
  slug: string,
  file: File,
): Promise<MapNodeRow> {
  const ext = screenshotExtForFile(file);
  const storagePath = `${slug}${ext}`;
  const contentType =
    file.type && file.type.startsWith('image/')
      ? file.type
      : ext === '.png'
        ? 'image/png'
        : ext === '.webp'
          ? 'image/webp'
          : 'image/jpeg';

  const { error: uploadError } = await supabase.storage
    .from(SYSTEM_MAP_STORAGE_BUCKET)
    .upload(storagePath, file, {
      contentType,
      upsert: true,
      cacheControl: '3600',
    });
  if (uploadError) throw uploadError;

  const { data: urlData } = supabase.storage.from(SYSTEM_MAP_STORAGE_BUCKET).getPublicUrl(storagePath);

  const { data, error } = await supabase
    .from('map_nodes')
    .update({
      screenshot_url: urlData.publicUrl,
      captured_at: new Date().toISOString(),
      source_hash: `upload-${Date.now()}`,
      show_thumbnail: true,
    })
    .eq('id', nodeId)
    .select('*')
    .single();

  if (error) throw error;
  return mapMapNodeRow(data as Record<string, unknown>);
}

export async function updateMapEdge(
  edgeId: string,
  updates: { label?: string | null; metadata?: MapEdgeMetadata },
): Promise<MapEdgeRow> {
  const payload: Record<string, unknown> = {};
  if ('label' in updates) payload.label = updates.label;
  if ('metadata' in updates) payload.metadata = updates.metadata ?? {};

  const { data, error } = await supabase
    .from('map_edges')
    .update(payload)
    .eq('id', edgeId)
    .select('*')
    .single();

  if (error) throw error;
  return mapMapEdgeRow(data as Record<string, unknown>);
}

export async function deleteMapEdge(edgeId: string): Promise<void> {
  const { error } = await supabase.from('map_edges').delete().eq('id', edgeId);
  if (error) throw error;
}

export const MAP_CONNECT_SOURCE_HANDLE = 'connect-out';
export const MAP_CONNECT_TARGET_HANDLE = 'connect-in';

export interface CreateMapEdgeInput {
  mapId: string;
  sourceNodeId: string;
  targetNodeId: string;
  edgeType?: MapEdgeType;
  label?: string | null;
}

export async function createMapEdge(input: CreateMapEdgeInput): Promise<MapEdgeRow> {
  if (input.sourceNodeId === input.targetNodeId) {
    throw new Error('Cannot connect a node to itself');
  }

  const payload = {
    map_id: input.mapId,
    source_node_id: input.sourceNodeId,
    target_node_id: input.targetNodeId,
    edge_type: input.edgeType ?? 'data',
    label: input.label?.trim() || null,
  };

  const { data, error } = await supabase.from('map_edges').insert(payload).select('*').single();
  if (error) throw error;
  return mapMapEdgeRow(data as Record<string, unknown>);
}

export function searchMapNodes(nodes: MapNodeRow[], query: string): MapNodeRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return nodes;
  return nodes.filter(
    (node) =>
      node.label.toLowerCase().includes(q) ||
      node.slug.toLowerCase().includes(q) ||
      node.kind.toLowerCase().includes(q) ||
      node.layer.toLowerCase().includes(q) ||
      node.role_description?.toLowerCase().includes(q) ||
      node.usage_notes?.toLowerCase().includes(q) ||
      node.url?.toLowerCase().includes(q) ||
      node.repo_path?.toLowerCase().includes(q) ||
      node.annotations.automation?.automationAlias?.toLowerCase().includes(q) ||
      node.annotations.automation?.steps.some(
        (step) =>
          step.name?.toLowerCase().includes(q) ||
          step.scheduleLabel.toLowerCase().includes(q),
      ),
  );
}

export function nodeMatchesQuery(node: MapNodeRow, query: string): boolean {
  if (!query.trim()) return false;
  return searchMapNodes([node], query).length > 0;
}
